/**
 * 产品验证查询页面 - 中式典雅风格
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 */
import React, { useState, useRef } from 'react';
import {
  Input,
  Button,
  Card,
  Typography,
  Space,
  Spin,
  Alert,
  Tag,
  Descriptions,
  Row,
  Col,
  Modal,
  message,
} from 'antd';
import {
  SearchOutlined,
  ScanOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { BarcodeDetectorPolyfill } from '@undecaf/barcode-detector-polyfill';
import StorefrontLayout from '../../components/storefront/StorefrontLayout';

// 初始化 polyfill（浏览器不支持原生 BarcodeDetector 时使用 ZBar WASM）
try {
  window['BarcodeDetector'].getSupportedFormats();
} catch {
  window['BarcodeDetector'] = BarcodeDetectorPolyfill;
}

const { Title, Text, Link } = Typography;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
  ink: '#2C2C2C',
  lightInk: '#5A5A5A',
};

// 字段映射（中文显示）
const fieldLabels = {
  country: '国家',
  facDate: '生产日期',
  productCode: '产品代码',
  productName: '产品名称',
  productType: '产品类型',
  queryTimes: '查询次数',
  serialNumber: '序列号',
  type: '类型',
};

// 隐藏的字段列表
const hiddenFields = ['newCode', 'verifyStatus'];

export default function ProductVerifyPage() {
  const [productCode, setProductCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isFake, setIsFake] = useState(false);
  const [scannerVisible, setScannerVisible] = useState(false);
  const [scannerLoading, setScannerLoading] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafIdRef = useRef(null);

  /** 查询产品 */
  const queryProduct = async (code) => {
    const codeToQuery = code || productCode.trim();

    if (!codeToQuery) {
      setError('请输入产品代码');
      return;
    }

    if (codeToQuery.length !== 14) {
      setError('产品代码必须是14位');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setIsFake(false);

    try {
      const response = await fetch('https://ec-api.offlinesass.com/auth/product/verify', {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/javascript, */*',
          'Content-Type': 'application/json',
          'customdata': JSON.stringify({ "distinct_id": "1987312b9aecbb-08f35ba781948b-4c657b58-1327104-1987312b9af19d9" }),
          'deptstr': 'ArchSD',
          'http_dpdeptid': 'ArchSD',
          'http_uid': 'archsd-bd-approver1',
          'uidstr': 'archsd-bd-approver1'
        },
        body: JSON.stringify({
          "store": "global",
          "cookie": "1770606377626aQUzTt2Y4lt0la1ZW3hxiIHY3L",
          "region": "global",
          "shopCode": "global",
          "returnTo": "/account",
          "webLanguage": "en",
          "code": codeToQuery,
          "recaptcha": "",
          "cookieId": "i92tt4vq9m1770606377704",
          "scanScenes": 0,
          "shopSource": "global",
          "country": "sg",
          "regionValue": 2
        })
      });

      const data = await response.json();

      if (data.code === 200 && data.data) {
        setResult(data.data);
      } else if (data.code === 500 && data.msg === '假货') {
        setIsFake(true);
      } else {
        setError(data.msg || '查询失败');
      }
    } catch (err) {
      setError('网络请求失败: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  /** 处理输入框回车 */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      queryProduct();
    }
  };

  /** 开始扫码 */
  const startScanner = async () => {
    const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (!isSecure) {
      Modal.warning({
        title: '无法使用扫码功能',
        content: (
          <div>
            <p>摄像头访问需要 HTTPS 安全连接。</p>
            <p style={{ marginTop: 8, color: COLORS.lightInk }}>
              请在 HTTPS 环境下访问本页面，或手动输入产品代码进行查询。
            </p>
          </div>
        ),
      });
      return;
    }

    setScannerVisible(true);
    setScannerLoading(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } },
      });

      streamRef.current = stream;

      const video = videoRef.current;
      video.srcObject = stream;
      await video.play();

      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      setScannerLoading(false);

      const scanFrame = () => {
        if (!streamRef.current || !videoRef.current) return;

        detector.detect(videoRef.current).then((results) => {
          if (results.length > 0) {
            handleScanSuccess(results[0].rawValue);
            return;
          }
          rafIdRef.current = requestAnimationFrame(scanFrame);
        }).catch(() => {
          rafIdRef.current = requestAnimationFrame(scanFrame);
        });
      };

      rafIdRef.current = requestAnimationFrame(scanFrame);
    } catch (err) {
      setScannerVisible(false);
      setScannerLoading(false);
      message.error('无法启动摄像头: ' + err.message);
    }
  };

  /** 停止扫码 */
  const stopScanner = () => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setScannerVisible(false);
    setScannerLoading(false);
  };

  /** 扫码成功处理 */
  const handleScanSuccess = (decodedText) => {
    stopScanner();

    let code = decodedText;

    if (code.includes('c=')) {
      const match = code.match(/c=([A-Za-z0-9]+)/);
      if (match && match[1]) {
        code = match[1];
      }
    }

    if (code.includes('&')) {
      code = code.split('&')[0];
    }

    setProductCode(code);
    queryProduct(code);
  };

  return (
    <StorefrontLayout>
      <div style={{
        maxWidth: 600,
        margin: '0 auto',
        padding: '32px 24px',
      }}>
        {/* 页面标题 */}
        <Title
          level={2}
          style={{
            textAlign: 'center',
            color: COLORS.ink,
            marginBottom: 24,
            letterSpacing: '0.2em',
            fontWeight: 300,
          }}
        >
          产品验证查询
        </Title>

        {/* 使用说明 */}
        <Card
          style={{
            background: `${COLORS.teaBrown}08`,
            borderRadius: 12,
            marginBottom: 24,
            border: `1px solid ${COLORS.teaBrown}15`,
          }}
          bodyStyle={{ padding: 16 }}
        >
          <Space direction="vertical" size={8}>
            <Text strong style={{ color: COLORS.ink }}>
              此查询调用官网API，无视IP和地区
            </Text>
            <Text style={{ color: COLORS.lightInk }}>
              此服务由 <Link href="https://ylyh666.com" target="_blank" style={{ color: COLORS.teaBrown }}>悦来悦好</Link> 提供，保真保正
            </Text>
            <Text style={{ color: COLORS.lightInk, fontSize: 13 }}>
              在输入框中输入14位产品代码，然后点击"查询"按钮获取产品信息。
            </Text>
            
          </Space>
        </Card>

        {/* 输入区域 */}
        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
          <Input
            placeholder="请输入14位产品代码"
            maxLength={14}
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
            onKeyPress={handleKeyPress}
            size="large"
            style={{
              borderRadius: 8,
              border: `2px solid ${COLORS.teaBrown}30`,
            }}
          />
        </Space.Compact>

        {/* 按钮区域 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              size="large"
              loading={loading}
              onClick={() => queryProduct()}
              block
              style={{
                background: COLORS.teaBrown,
                borderColor: COLORS.teaBrown,
                borderRadius: 8,
              }}
            >
              查询
            </Button>
          </Col>
          <Col span={12}>
            <Button
              icon={<ScanOutlined />}
              size="large"
              onClick={startScanner}
              block
              style={{
                borderRadius: 8,
                borderColor: COLORS.teaBrown,
                color: COLORS.teaBrown,
              }}
            >
              扫码查询
            </Button>
          </Col>
        </Row>

        {/* 加载状态 */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin size="large" />
            <Text style={{ display: 'block', marginTop: 16, color: COLORS.lightInk }}>
              正在查询...
            </Text>
          </div>
        )}

        {/* 错误提示 */}
        {error && !loading && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ borderRadius: 8, marginBottom: 24 }}
          />
        )}

        {/* 假货警告 */}
        {isFake && !loading && (
          <Card
            style={{
              background: COLORS.cinnabar,
              borderRadius: 12,
              marginBottom: 24,
              border: 'none',
            }}
            bodyStyle={{ padding: 24, textAlign: 'center' }}
          >
            <WarningOutlined style={{ fontSize: 48, color: '#fff', marginBottom: 16 }} />
            <Title level={4} style={{ color: '#fff', marginBottom: 8 }}>
              此产品为假货
            </Title>
            <Text style={{ color: '#fff', opacity: 0.9 }}>
              该产品代码验证失败，非正品产品
            </Text>
          </Card>
        )}

        {/* 查询成功结果 */}
        {result && !loading && (
          <Card
            style={{
              background: '#fff',
              borderRadius: 12,
              marginBottom: 24,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            }}
            bodyStyle={{ padding: 20 }}
          >
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Title level={5} style={{ margin: 0, color: COLORS.ink }}>
                查询结果
              </Title>
              <Tag color={COLORS.bambooGreen} style={{ borderRadius: 4 }}>
                <CheckCircleOutlined /> 成功
              </Tag>
            </div>

            <Descriptions
              column={1}
              labelStyle={{ fontWeight: 500, color: COLORS.lightInk, width: 120 }}
              contentStyle={{ color: COLORS.ink }}
            >
              {Object.entries(result)
                .filter(([key]) => !hiddenFields.includes(key))
                .map(([key, value]) => (
                  <Descriptions.Item key={key} label={fieldLabels[key] || key}>
                    {value !== null ? value : '无'}
                  </Descriptions.Item>
                ))}
            </Descriptions>
          </Card>
        )}

        {/* 扫码弹窗 */}
        <Modal
          open={scannerVisible}
          onCancel={stopScanner}
          footer={null}
          width={500}
          centered
          title={null}
          closable={false}
          bodyStyle={{ padding: 0, borderRadius: 12 }}
          style={{ borderRadius: 12 }}
        >
          <div style={{
            position: 'relative',
            width: '100%',
            height: 450,
            background: 'rgba(0,0,0,0.9)',
            borderRadius: 12,
          }}>
            <Button
              type="text"
              icon={<CloseOutlined style={{ fontSize: 24, color: '#fff' }} />}
              onClick={stopScanner}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: '#fff',
                borderRadius: '50%',
                width: 40,
                height: 40,
                zIndex: 10,
              }}
            />
            {scannerLoading && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 5,
              }}>
                <Spin size="large" />
              </div>
            )}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: 450,
                objectFit: 'cover',
                borderRadius: 12,
              }}
            />
            <Text style={{
              color: '#fff',
              fontSize: 14,
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              background: 'rgba(0,0,0,0.5)',
              padding: '4px 12px',
              borderRadius: 4,
            }}>
              请将二维码对准框内
            </Text>
          </div>
        </Modal>
      </div>
    </StorefrontLayout>
  );
}